import TeamLogo from './TeamLogo';
import { deriveMatchSetState, isStaleLiveFeed } from '../data/matchState';
import { broadcastLabel, gameBroadcastLabel } from '../data/broadcast';

const SET_NUMBERS = [
  1,
  2,
  3,
  4,
  5,
];

function hasValue(value) {
  return (
    value !== null &&
    value !== undefined &&
    value !== ''
  );
}

function normalizeSetScores(team) {
  if (
    !Array.isArray(
      team?.setScores
    )
  ) {
    return [];
  }

  return team.setScores
    .slice(0, 5)
    .map((score) => {
      if (!hasValue(score)) {
        return null;
      }

      const numericScore =
        Number(score);

      return Number.isFinite(
        numericScore
      )
        ? numericScore
        : null;
    });
}

function didWinSet(
  score,
  opponentScore,
  setComplete = true
) {
  return (
    setComplete &&
    Number.isFinite(score) &&
    Number.isFinite(
      opponentScore
    ) &&
    score > opponentScore
  );
}

function LocationIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M12 2a7 7 0 0 0-7 7c0 5.1 6.1 11.7 6.4 12a.8.8 0 0 0 1.2 0C12.9 20.7 19 14.1 19 9a7 7 0 0 0-7-7Zm0 10.2A3.2 3.2 0 1 1 12 5.8a3.2 3.2 0 0 1 0 6.4Z"
      />
    </svg>
  );
}

function BroadcastIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-6v2h-4v-2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm0 2v10h16V7H4Z"
      />
    </svg>
  );
}

function TeamRow({
  team,
  side,
  opponentSetScores,
  winner,
  setState,
  isFavorite,
  onToggleFavorite,
}) {
  const teamName =
    team?.name || 'TBD';

  const setScores =
    normalizeSetScores(
      team
    );

  const canFavorite =
    typeof onToggleFavorite ===
      'function' &&
    Boolean(team?.name);

  function handleFavoriteClick(
    event
  ) {
    event.preventDefault();
    event.stopPropagation();

    if (!canFavorite) {
      return;
    }

    onToggleFavorite(team);
  }

  return (
    <div
      className={`team-row team-row-${side}${
        winner ? ' winner' : ''
      }`}
    >
      <div className="team-identity">
        <TeamLogo team={team} />

        <div className="team-info">
          <strong className="team-name">
            {team?.rank ? (
              <span className="team-rank">
                #{team.rank}
              </span>
            ) : null}

            <span>
              {teamName}
            </span>
          </strong>

          <div className="team-meta">
            {team?.record ? (
              <small>
                {team.record}
              </small>
            ) : null}

            {team?.conference ? (
              <span className="conference-pill">
                {team.conference}
              </span>
            ) : null}
          </div>
        </div>

        {canFavorite ? (
          <button
            type="button"
            className={`team-favorite-button${
              isFavorite
                ? ' active'
                : ''
            }`}
            onClick={
              handleFavoriteClick
            }
            aria-label={
              isFavorite
                ? `Remove ${teamName} from favorites`
                : `Add ${teamName} to favorites`
            }
            aria-pressed={
              isFavorite
            }
            title={
              isFavorite
                ? 'Remove from favorites'
                : 'Add to favorites'
            }
          >
            <span aria-hidden="true">
              {isFavorite
                ? '★'
                : '☆'}
            </span>
          </button>
        ) : null}
      </div>

      <div
        className="set-scores set-score-grid"
        aria-label={
          setScores.length
            ? `${teamName} set scores: ${setScores
                .filter(
                  Number.isFinite
                )
                .join(', ')}`
            : `${teamName} set scores unavailable`
        }
      >
        {SET_NUMBERS.map(
          (setNumber, index) => {
            const score =
              setScores[index];

            const opponentScore =
              opponentSetScores[
                index
              ];

            const derivedSet = setState?.sets?.[index];
            const setIsCurrent = derivedSet?.state === 'in-progress';
            const setComplete = derivedSet?.state === 'completed';

            const wonSet =
              didWinSet(
                score,
                opponentScore,
                setComplete
              );

            return (
              <span
                key={`${teamName}-set-${setNumber}`}
                className={`set-score-cell${
                  wonSet
                    ? ' won'
                    : ''
                }${
                  setIsCurrent
                    ? ' current'
                    : ''
                }${
                  !Number.isFinite(
                    score
                  )
                    ? ' empty'
                    : ''
                }`}
                aria-label={
                  Number.isFinite(
                    score
                  )
                    ? `Set ${setNumber}: ${score}${
                        wonSet
                          ? ', won'
                          : setIsCurrent
                            ? ', in progress'
                          : ''
                      }`
                    : `Set ${setNumber}: ${setIsCurrent ? 'in progress' : 'not played'}`
                }
              >
                {Number.isFinite(
                  score
                )
                  ? score
                  : '—'}
              </span>
            );
          }
        )}
      </div>

      <strong
        className="match-score"
        aria-label={`${teamName} sets won: ${
          setState?.phase === 'not-started' ? 'not started' : team?.score ?? 'not listed'
        }`}
      >
        {setState?.phase === 'not-started' ? '—' : team?.score ?? '—'}
      </strong>
    </div>
  );
}

function getValidGameId(game) {
  const candidates = [
    game?.gameId,
    game?.id,
  ];

  for (const candidate of candidates) {
    const value = String(
      candidate || ''
    ).trim();

    if (
      /^[1-9]\d*$/.test(
        value
      )
    ) {
      return value;
    }
  }

  return '';
}

function hasText(value) {
  return Boolean(
    String(value || '').trim()
  );
}

export default function ScoreCard({
  game,
  onOpenMatch,
  isTeamFavorite,
  onToggleFavoriteTeam,
  hideStatusLabel = false,
}) {
  const isFinal =
    game?.status === 'final';

  const setState = deriveMatchSetState(game);

  const competitionLabel =
    game?.competition?.countsTowardRecord === false
      ? game.competition.label || 'Exhibition'
      : '';

  const awayScore =
    hasValue(game?.away?.score) && Number.isFinite(
      Number(game?.away?.score)
    )
      ? Number(
          game.away.score
        )
      : null;

  const homeScore =
    hasValue(game?.home?.score) && Number.isFinite(
      Number(game?.home?.score)
    )
      ? Number(
          game.home.score
        )
      : null;

  const awayWon =
    isFinal &&
    (
      game?.away?.winner ===
        true ||
      (
        awayScore !== null &&
        homeScore !== null &&
        awayScore > homeScore
      )
    );

  const homeWon =
    isFinal &&
    (
      game?.home?.winner ===
        true ||
      (
        awayScore !== null &&
        homeScore !== null &&
        homeScore > awayScore
      )
    );

  const matchLabel =
    `${
      game?.away?.name ||
      'Away'
    } vs. ` +
    `${
      game?.home?.name ||
      'Home'
    }`;

  const gameId =
    getValidGameId(game);

  const matchCenterAvailable =
    Boolean(gameId) &&
    game?.matchCenterAvailable !==
      false;

  const canOpenVolleyWireMatchCenter =
    matchCenterAvailable &&
    typeof onOpenMatch ===
      'function';

  const canOpenNcaaMatchCenter =
    matchCenterAvailable &&
    !canOpenVolleyWireMatchCenter &&
    hasText(game?.gameUrl);

  const awayFavorite =
    typeof isTeamFavorite ===
      'function'
      ? isTeamFavorite(
          game?.away
        )
      : false;

  const homeFavorite =
    typeof isTeamFavorite ===
      'function'
      ? isTeamFavorite(
          game?.home
        )
      : false;

  const awaySetScores =
    normalizeSetScores(
      game?.away
    );

  const homeSetScores =
    normalizeSetScores(
      game?.home
    );

  const venue =
    String(
      game?.venue ||
      game?.location ||
      game?.site ||
      ''
    ).trim();

  const network = broadcastLabel(
    gameBroadcastLabel(game),
    game?.network,
    game?.networks,
    game?.broadcasterName,
    game?.broadcast,
    game?.broadcasts,
    game?.tv,
    game?.television,
    game?.media,
    game?.video,
    game?.liveVideo,
    game?.liveVideos,
    game?.stream,
    game?.streaming,
    game?.streamingProvider,
    game?.webcast,
    game?.webcasts,
    game?.watch,
    game?.watchInfo,
    game?.coverage
  );

  const publishedTime = String(game?.timeLabel || '').trim();
  const footerTime = game?.status !== 'scheduled' && /^(?:time\s*)?(?:tba|tbd|unknown)$/i.test(publishedTime)
    ? '' : publishedTime || (game?.status === 'scheduled' ? 'Time TBD' : '');

  const currentSet = setState.activeSet;

  const hasSetScores =
    awaySetScores.some(
      Number.isFinite
    ) ||
    homeSetScores.some(
      Number.isFinite
    ) ||
    Number.isInteger(currentSet);

  function handleOpenMatch() {
    if (
      !canOpenVolleyWireMatchCenter
    ) {
      return;
    }

    onOpenMatch({
      ...game,
      id: gameId,
      gameId,
    });
  }

  function handleCardClick() {
    handleOpenMatch();
  }

  function handleActionClick(
    event
  ) {
    event.preventDefault();
    event.stopPropagation();

    handleOpenMatch();
  }

  function handleExternalClick(
    event
  ) {
    event.stopPropagation();
  }

  return (
    <article
      className={`score-card${
        canOpenVolleyWireMatchCenter
          ? ' score-card-clickable'
          : ''
      } score-card-${game?.status || 'scheduled'}`}
      onClick={
        canOpenVolleyWireMatchCenter
          ? handleCardClick
          : undefined
      }
      style={
        canOpenVolleyWireMatchCenter
          ? {
              cursor:
                'pointer',
            }
          : undefined
      }
      aria-label={`${matchLabel}. ${
        game?.statusLabel ||
        'Upcoming'
      }.`}
    >
      <header className="score-card-topline">
        <span className="score-card-status-group">
          {hideStatusLabel ? null : (
            <span
              className={`status-pill ${game?.status || 'scheduled'}`}
            >
              {game?.statusLabel || 'Upcoming'}
            </span>
          )}
          {competitionLabel ? (
            <span className="competition-pill">{competitionLabel}</span>
          ) : null}
        </span>

      </header>

      {hasSetScores ? (
        <div
          className="score-card-set-headings"
          aria-hidden="true"
        >
          <span />

          <div className="set-score-labels">
            {SET_NUMBERS.map(
              (setNumber) => (
                <span
                  key={`set-heading-${setNumber}`}
                  className={
                    setState.sets?.[setNumber - 1]?.state === 'in-progress'
                      ? 'current'
                      : ''
                  }
                >
                  {setNumber}
                </span>
              )
            )}
          </div>

          <strong>
            Sets
          </strong>
        </div>
      ) : null}

      <TeamRow
        team={game?.away}
        side="away"
        opponentSetScores={
          homeSetScores
        }
        winner={awayWon}
        setState={setState}
        isFavorite={
          awayFavorite
        }
        onToggleFavorite={
          onToggleFavoriteTeam
        }
      />

      <TeamRow
        team={game?.home}
        side="home"
        opponentSetScores={
          awaySetScores
        }
        winner={homeWon}
        setState={setState}
        isFavorite={
          homeFavorite
        }
        onToggleFavorite={
          onToggleFavoriteTeam
        }
      />

      <footer>
        <div className="game-details">
          {game.statusNote ? <span className="score-feed-warning" title={game.statusNote}>Last available score · {game.statusLabel}</span> : null}
          {isStaleLiveFeed(game) ? <span className="score-feed-warning" title="This older match is still marked live by its source. A final result has not been confirmed.">Live status unconfirmed</span> : null}
          {game.scoreSource === 'official-school-result' && game.sourceUrl ? (
            <a className="score-verified-source" href={game.sourceUrl} target="_blank" rel="noreferrer" onClick={handleExternalClick}>Verified result</a>
          ) : null}
          {footerTime ? (
            <strong className="score-card-footer-time">{footerTime}</strong>
          ) : null}

          {venue ? (
            <span
              className="venue-detail"
              title={venue}
            >
              <LocationIcon />

              <span>
                {venue}
              </span>
            </span>
          ) : null}

          {network ? (
            <span
              className="network"
              title={network}
            >
              <BroadcastIcon />

              <span>
                {network}
              </span>
            </span>
          ) : null}

        </div>

        {canOpenVolleyWireMatchCenter ? (
          <button
            type="button"
            className="match-center-link"
            onClick={
              handleActionClick
            }
            aria-label={`Open Match Center for ${matchLabel}`}
          >
            <span>Match Center</span>
            <span
              className="match-center-chevron"
              aria-hidden="true"
            >
              ›
            </span>
          </button>
        ) : canOpenNcaaMatchCenter ? (
          <a
            className="match-center-link"
            href={game.gameUrl}
            target="_blank"
            rel="noreferrer"
            onClick={
              handleExternalClick
            }
            aria-label={`View ${matchLabel} on NCAA.com`}
          >
            <span>Match Center</span>
            <span
              className="match-center-chevron"
              aria-hidden="true"
            >
              ›
            </span>
          </a>
        ) : (
          <button
            type="button"
            className="match-center-link"
            disabled
            aria-label={`Match Center unavailable for ${matchLabel}`}
          >
            <span>Match Center</span>
            <span
              className="match-center-chevron"
              aria-hidden="true"
            >
              –
            </span>
          </button>
        )}
      </footer>
    </article>
  );
}
